import {VisuallyHidden} from '@commercial-helios/ui/src/components/visually-hidden'
import {tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import closeIcon from '@helios/shared/images/icons/close-black.svg'
import documentIcon from '@helios/shared/images/icons/document.svg'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Label} from '@helios/shared/styles/inputs'
import {Text} from '@helios/shared/styles/typography'
import {focusStyles} from '@helios/shared/styles/utils'
import replacePlaceholders from '@helios/shared/utils/replace-placeholders'

interface Props {
    id: string
    allowedFileTypes: string[]
    label: string
    uploadText: string
    fileTypesText: string
    error: string | null
    fileSize?: number
    'data-testid'?: string
    onChange: (file: File) => void
    required?: boolean
}

export const FileUpload = ({
    id,
    allowedFileTypes,
    label,
    uploadText,
    fileTypesText,
    error,
    onChange,
    required,
    fileSize = 10,
    ...props
}: Props) => {
    const ref = React.useRef<HTMLInputElement>(null)
    const [file, setFile] = React.useState<File | null>(null)
    const [fileSizeError, setFileSizeError] = React.useState('')
    const globalStrings = useGlobalStrings()

    const fileTypesMessage = replacePlaceholders(fileTypesText, {
        '{{allowedFileTypes}}': allowedFileTypes.join(', '),
    })

    const handleChange = (uploadedFile: File) => {
        if (uploadedFile.size / 1024 ** 2 >= fileSize) {
            setFileSizeError(globalStrings.file_upload_too_large)
            return
        }

        setFileSizeError('')
        setFile(uploadedFile)
        onChange(uploadedFile)
    }

    const resetFileUpload = () => {
        if (ref.current) ref.current.value = ''

        setFile(null)
    }

    const accumulatedError = fileSizeError || error

    return (
        <StyledDiv>
            <input
                ref={ref}
                type="file"
                id={id}
                onChange={(e) => e.target.files && handleChange(e.target.files[0])}
                accept={`.${allowedFileTypes.join(',.')}`}
                data-testid={props['data-testid']}
                required={required}
            />
            <StyledLabel htmlFor={id}>
                <Label as="span" $required={required}>
                    {label}
                </Label>

                {!file && (
                    <Text as={StyledText} variant="label-base" color="brand">
                        {uploadText}
                    </Text>
                )}
            </StyledLabel>

            {file ? (
                <FileDetails>
                    <img src={documentIcon} alt="" />

                    <Text color="placeholder" variant="label-base">
                        {file.name}
                    </Text>

                    <CloseButton onClick={resetFileUpload}>
                        <VisuallyHidden>{globalStrings.close_button_text}</VisuallyHidden>
                        <img src={closeIcon} alt="" />
                    </CloseButton>
                </FileDetails>
            ) : (
                <Text color="placeholder" variant="label-base">
                    {fileTypesMessage}
                </Text>
            )}
            {accumulatedError && (
                <Text
                    variant="label-base"
                    color="error"
                    data-testid={props['data-testid'] && `${props['data-testid']}-error`}
                >
                    {accumulatedError}
                </Text>
            )}
        </StyledDiv>
    )
}

const StyledText = styled.span`
    &:hover {
        color: ${tokens.pink600};
    }
`

const StyledLabel = styled.label`
    span:first-of-type {
        display: block;
        margin-bottom: ${tokens.spacing10};
    }
`

const StyledDiv = styled.div`
    [type='file'] {
        position: absolute;
        width: 1px;
        height: 1px;
        outline: none;

        &:focus + ${StyledLabel} ${StyledText} {
            ${focusStyles()}
            border-radius: ${tokens.spacing4};
        }
    }
`

const FileDetails = styled.div`
    display: flex;
    align-items: center;
    gap: ${tokens.spacing4};
`

const CloseButton = styled.button`
    padding: 0;
    cursor: pointer;
    background-color: transparent;
    border: none;
    outline: none;

    &:focus {
        ${focusStyles()}
        border-radius: ${tokens.spacing4};
    }
`
